import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/context/AuthProvider";
import { useCompanyContext } from "../../common/company/CompanyContext";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import CompanyIdentityHeader from "../../common/company/CompanyIdentityHeader";
import CompanyEsgScopeController from "./CompanyEsgScopeController";

/**
 * 🎯 COMPANY ESG SCOPE
 * Step 1 of "how do we go from an ingested taxonomy to a declaration": a
 * company connects to an already-synced taxonomy version, then picks which
 * concepts out of that version's catalogue it will actually declare
 * against. This picked list is the company's own ESG Scope. Declaration
 * entry (values against each scope concept) is the next screen, built once
 * this exists.
 */
export default function CompanyEsgScopeView() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { activeCompanyId, activeCompanyName } = useCompanyContext();
  const siteKey = process.env.REACT_APP_SITE_KEY;
  const SITE_NAME = process.env.REACT_APP_SITE_NAME || "sustina.nrgpix.ch";

  const {
    loading,
    error,
    boundHeaderId,
    isConnected,
    headers,
    selectedHeaderId,
    setSelectedHeaderId,
    connecting,
    handleConnect,
    searchTerm,
    setSearchTerm,
    filteredConcepts,
    scope,
    scopedRowIds,
    busyTag,
    handleAddToScope,
    handleRemoveFromScope,
  } = CompanyEsgScopeController(activeCompanyId, siteKey);

  const handleSignOut = async () => {
    await logout();
    navigate("/auth/sign-in");
  };

  if (!activeCompanyId) {
    navigate("/registered/dashboard");
    return null;
  }

  return (
    <main id="main" className="page-main" data-scroll-container>
      <div className="page-main-inner">
        <section className="page-section">
          <div className="container mt-4">
            <div className="row mb-2">
              <div className="col">
                <h2 className="h4 fw-bold mb-1">Company ESG Scope</h2>
                <p className="text-muted small mb-0">which taxonomy concepts this company declares against</p>
              </div>
            </div>

            <CompanyIdentityHeader companyId={activeCompanyId} siteKey={siteKey} />

            {error && <div className="alert alert-danger extra-small">{error}</div>}

            {loading ? (
              <p className="text-muted extra-small italic">Loading...</p>
            ) : !isConnected ? (
              <div className="card shadow-sm border-0 bg-white p-3">
                <h6 className="extra-small fw-bold text-uppercase text-muted mb-2">
                  Connect a taxonomy version
                </h6>
                <p className="extra-small text-muted mb-3">
                  No ESG taxonomy connected yet. Pick an already-ingested version to build this company's scope from.
                </p>
                <div className="d-flex gap-2">
                  <select
                    className="form-select form-select-sm"
                    style={{ maxWidth: 360 }}
                    value={selectedHeaderId}
                    onChange={(e) => setSelectedHeaderId(e.target.value)}
                  >
                    <option value="">-- Select a taxonomy version --</option>
                    {headers.map((h, i) => {
                      const headerId = GoldenSourceHelper.get(h, "HeaderId");
                      return (
                        <option key={headerId || i} value={headerId}>
                          {GoldenSourceHelper.get(h, "VersionLabel")} ({GoldenSourceHelper.get(h, "TaxYear")})
                        </option>
                      );
                    })}
                  </select>
                  <button
                    className="btn btn-sm btn-primary fw-bold"
                    onClick={handleConnect}
                    disabled={connecting || !selectedHeaderId}
                  >
                    {connecting ? "CONNECTING..." : "Connect"}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <span className="badge bg-dark text-white extra-small">
                    CONNECTED &middot; HeaderId {boundHeaderId}
                  </span>
                  <span className="extra-small text-muted">{scope.length} concept(s) in scope</span>
                </div>

                <div className="row g-3">
                  {/* AVAILABLE CONCEPTS */}
                  <div className="col-md-7">
                    <div className="card shadow-sm border-0 bg-white overflow-hidden h-100">
                      <div className="p-3 border-bottom bg-light">
                        <h6 className="extra-small fw-bold text-uppercase text-primary mb-2">
                          Taxonomy Concepts
                        </h6>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Search by tag or label..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <div className="table-responsive" style={{ maxHeight: 480 }}>
                        <table className="table table-hover table-sm mb-0 align-middle">
                          <tbody>
                            {filteredConcepts.map((c, i) => {
                              const rowId = GoldenSourceHelper.get(c, "RowId");
                              const xbrlTag = GoldenSourceHelper.get(c, "XbrlTag");
                              const inScope = scopedRowIds.has(rowId);
                              return (
                                <tr key={rowId || i}>
                                  <td className="ps-3">
                                    <code className="extra-small text-primary fw-bold">{xbrlTag}</code>
                                    <div className="extra-small text-muted">{GoldenSourceHelper.get(c, "MeasureName")}</div>
                                  </td>
                                  <td className="text-end pe-3" style={{ width: 110 }}>
                                    <button
                                      className={`btn btn-sm fw-bold ${inScope ? "btn-outline-secondary" : "btn-outline-primary"}`}
                                      disabled={inScope || busyTag === rowId}
                                      onClick={() => handleAddToScope(c)}
                                    >
                                      {inScope ? "IN SCOPE" : busyTag === rowId ? "..." : "ADD"}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                            {filteredConcepts.length === 0 && (
                              <tr>
                                <td className="p-4 text-center text-muted extra-small italic">No concepts found.</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* CURRENT SCOPE */}
                  <div className="col-md-5">
                    <div className="card shadow-sm border-0 bg-white overflow-hidden h-100">
                      <div className="p-3 border-bottom bg-light">
                        <h6 className="extra-small fw-bold text-uppercase text-primary mb-0">
                          This Company's Scope
                        </h6>
                      </div>
                      <div className="table-responsive" style={{ maxHeight: 480 }}>
                        <table className="table table-hover table-sm mb-0 align-middle">
                          <tbody>
                            {scope.map((s, i) => {
                              const rowId = GoldenSourceHelper.get(s, "TaxonomyDetailRowId");
                              const xbrlTag = GoldenSourceHelper.get(s, "XbrlTag");
                              return (
                                <tr key={rowId || i}>
                                  <td className="ps-3">
                                    <code className="extra-small text-primary fw-bold">{xbrlTag}</code>
                                    <div className="extra-small text-muted">{GoldenSourceHelper.get(s, "MeasureName")}</div>
                                  </td>
                                  <td className="text-end pe-3" style={{ width: 90 }}>
                                    <button
                                      className="btn btn-sm btn-outline-danger fw-bold"
                                      disabled={busyTag === rowId}
                                      onClick={() => handleRemoveFromScope(rowId)}
                                    >
                                      {busyTag === rowId ? "..." : "REMOVE"}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                            {scope.length === 0 && (
                              <tr>
                                <td className="p-4 text-center text-muted extra-small italic">
                                  No concepts in scope yet — add some from the left.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* FOOTER NAVIGATION */}
            <div className="mt-5 pt-4 border-top d-flex justify-content-between align-items-center">
              <div className="d-flex gap-3">
                <button className="btn btn-link text-decoration-none p-0 text-primary fw-bold small" onClick={() => navigate("/registered/company-dashboard")}>
                  <i className="bi bi-arrow-left me-2"></i> Back to Dashboard
                </button>
                <button className="btn btn-link text-decoration-none p-0 text-primary fw-bold small" onClick={handleSignOut}>
                  <i className="bi bi-box-arrow-left me-2"></i> Sign Out
                </button>
              </div>
              <div className="text-end">
                <span className="text-muted extra-small d-block">Workflow Context</span>
                <span className="fw-bold small">{activeCompanyName || SITE_NAME}</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
